import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { BLOG_POSTS } from "@/lib/blog";
import { usePageSEO } from "@/hooks/use-page-seo";

const Blog = () => {
  const heroRef = useRef<HTMLElement>(null);

  usePageSEO({
    title: "Yacht Rock Blog — Guides, Playlists & Detroit Events | Yacht Rock Detroit",
    description: "Explore yacht rock culture, curated playlists, Detroit summer concert guides, and everything you need to know about Yacht Rock Detroit 2026.",
    keywords: "yacht rock blog, detroit music blog, yacht rock playlist, summer concert guide",
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Lifestyle", url: "https://yachtrockdetroit.com/lifestyle" },
      { name: "Blog", url: "https://yachtrockdetroit.com/lifestyle/blog" },
    ],
  });

  return (
    <main className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={heroRef} />

      {/* Hero */}
      <section ref={heroRef} className="bg-yacht-blue pt-32 md:pt-40 pb-16 md:pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-yacht-cream/10 backdrop-blur-sm border border-yacht-cream/20 rounded-full px-4 py-1.5 text-xs font-medium text-yacht-cream mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-yacht-yellow" />
            Blog
          </span>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-yacht-cream leading-tight">
            Lifestyle & Insights
          </h1>
          <p className="text-yacht-cream/60 text-sm md:text-base max-w-md mx-auto mt-4">
            Stories, guides, and everything yacht rock.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {BLOG_POSTS.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/lifestyle/blog/${post.slug}`}
                  className="group block"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex items-center bg-yacht-charcoal text-yacht-cream text-[10px] font-semibold uppercase tracking-wider rounded-full px-3 py-1">
                      {post.category}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {post.readTime}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="font-serif text-lg md:text-xl text-yacht-charcoal leading-snug group-hover:text-yacht-blue transition-colors">
                    {post.title}
                  </h2>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Blog;
